/**
 * Service Pricing Service
 * Handles CRUD operations for service pricing management (ClientAdmin only)
 */

import { API_ENDPOINTS } from '../config/api';
import { makeAuthenticatedRequest, createApiError } from './apiService';

export interface ServicePricing {
  id: string;
  serviceType: string;
  stateCode: string | null;
  recordType: string | null;
  costPerLookup: number;
  isActive: boolean;
  description: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateServicePricingRequest {
  serviceType: string;
  stateCode?: string | null;
  recordType?: string | null;
  costPerLookup: number;
  isActive?: boolean;
  description?: string | null;
}

export interface UpdateServicePricingRequest {
  costPerLookup?: number;
  isActive?: boolean;
  description?: string | null;
}

/**
 * List all service pricing entries, optionally filtered by service type
 */
export const listServicePricings = async (serviceType?: string): Promise<ServicePricing[]> => {
  let url = API_ENDPOINTS.SERVICE_PRICINGS.LIST;
  if (serviceType) {
    url += `?serviceType=${encodeURIComponent(serviceType)}`;
  }

  const response = await makeAuthenticatedRequest(url);

  if (!response.ok) {
    const error = await createApiError(response);
    throw error;
  }

  return await response.json();
};

/**
 * Create a new service pricing entry
 */
export const createServicePricing = async (data: CreateServicePricingRequest): Promise<ServicePricing> => {
  const response = await makeAuthenticatedRequest(API_ENDPOINTS.SERVICE_PRICINGS.CREATE, {
    method: 'POST',
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await createApiError(response);
    throw error;
  }

  return await response.json();
};

/**
 * Update an existing service pricing entry
 */
export const updateServicePricing = async (id: string, data: UpdateServicePricingRequest): Promise<ServicePricing> => {
  const response = await makeAuthenticatedRequest(API_ENDPOINTS.SERVICE_PRICINGS.UPDATE(id), {
    method: 'PUT',
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await createApiError(response);
    throw error;
  }

  return await response.json();
};

/**
 * Delete a service pricing entry
 */
export const deleteServicePricing = async (id: string): Promise<void> => {
  const response = await makeAuthenticatedRequest(API_ENDPOINTS.SERVICE_PRICINGS.DELETE(id), {
    method: 'DELETE',
  });

  if (!response.ok) {
    const error = await createApiError(response);
    throw error;
  }
};
